'use client'

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

export type Language = 'fr' | 'en'
export type CurrencyCode = 'TND' | 'EUR' | 'USD'

type LanguageContextValue = {
  language: Language
  country: string | null
  currencyCode: CurrencyCode
  currencySymbol: string | null
  callingCode: string
  timezone: string
  setLanguage: (lang: Language) => void
  setCurrency: (code: CurrencyCode) => void
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined)

const francophoneCountries = new Set([
  'FR',
  'TN',
  'DZ',
  'MA',
  'BE',
  'CH',
  'CA',
  'LU',
  'MC',
  'SN',
  'CI',
  'CM',
  'CD',
  'CG',
  'GA',
  'GN',
  'ML',
  'NE',
  'TG',
  'BJ',
  'BF',
  'CF',
  'KM',
  'DJ',
  'GQ',
  'MG',
  'MU',
  'SC',
  'TD',
])

function detectBrowserLanguage(): Language {
  if (typeof navigator === 'undefined') return 'fr'
  const candidates = navigator.languages?.length ? navigator.languages : [navigator.language]
  const normalized = candidates
    .map((lang) => (lang || '').toLowerCase())
    .filter(Boolean)
  const prefersFrench = normalized.some((lang) => lang.startsWith('fr'))
  return prefersFrench ? 'fr' : 'en'
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [country, setCountry] = useState<string | null>(null)
  const [language, setLanguageState] = useState<Language>('fr')
  const [currencyCode, setCurrencyCode] = useState<CurrencyCode>('EUR')
  const [currencySymbol, setCurrencySymbol] = useState<string | null>('€')
  const [callingCode, setCallingCode] = useState('+33')
  const [timezone, setTimezone] = useState('Europe/Paris')

  const setCookie = (key: string, value: string) => {
    if (typeof document === 'undefined') return
    document.cookie = `${key}=${encodeURIComponent(value)}; path=/; max-age=31536000`
  }

  const normalizeCurrency = (value?: string | null): CurrencyCode => {
    const normalized = String(value || '').toUpperCase()
    if (normalized === 'TND' || normalized === 'USD' || normalized === 'EUR') return normalized
    return 'EUR'
  }

  useEffect(() => {
    if (typeof window === 'undefined') return
    const storedLang = localStorage.getItem('syris_lang') as Language | null
    const storedCountry = localStorage.getItem('syris_country')
    const storedGeo = localStorage.getItem('syris_geo')
    const storedCurrency = localStorage.getItem('syris_currency')
    if (storedCountry) {
      setCountry(storedCountry)
    }
    if (storedGeo) {
      try {
        const parsed = JSON.parse(storedGeo)
        const parsedCountry = parsed?.country_code ? String(parsed.country_code).toUpperCase() : null
        const nextCalling = parsed?.country_calling_code || parsed?.countryCallingCode
        const nextTimezone = parsed?.timezone
        const nextCurrency = normalizeCurrency(parsed?.currency)
        const nextSymbol = parsed?.currency_symbol || parsed?.currencySymbol || null
        if (parsedCountry) {
          setCountry(parsedCountry)
          localStorage.setItem('syris_country', parsedCountry)
          setCookie('syris_country', parsedCountry)
        }
        if (nextCalling) {
          setCallingCode(nextCalling)
          setCookie('syris_calling_code', nextCalling)
        }
        if (nextTimezone) {
          setTimezone(nextTimezone)
          setCookie('syris_timezone', nextTimezone)
        }
        if (nextCurrency) {
          const nextCode = storedCurrency ? normalizeCurrency(storedCurrency) : nextCurrency
          setCurrencyCode(nextCode)
          setCookie('syris_currency', nextCode)
          setCurrencySymbol(storedCurrency ? symbolMap[nextCode] : nextSymbol || symbolMap[nextCode])
        }
      } catch {}
    }
    if (storedCurrency) {
      const nextCode = normalizeCurrency(storedCurrency)
      setCurrencyCode(nextCode)
      setCurrencySymbol(symbolMap[nextCode])
      setCookie('syris_currency', nextCode)
    }
    const inferredFromCountry = storedCountry && francophoneCountries.has(storedCountry) ? 'fr' : 'en'
    const nextLang = storedLang || (storedCountry ? inferredFromCountry : detectBrowserLanguage())
    setLanguageState(nextLang)
    localStorage.setItem('syris_lang', nextLang)
    document.documentElement.lang = nextLang
    setCookie('syris_lang', nextLang)

    if (!storedGeo) {
      fetch('/web-api/geo')
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (!data) return
          const code = data?.country_code ? String(data.country_code).toUpperCase() : null
          const nextCalling = data?.country_calling_code || '+33'
          const nextCurrency = normalizeCurrency(data?.currency)
          const nextSymbol = data?.currency_symbol || null
          const nextTimezone = data?.timezone || 'Europe/Paris'
          if (code) {
            setCountry(code)
            localStorage.setItem('syris_country', code)
            setCookie('syris_country', code)
          }
          setCallingCode(nextCalling)
          setCookie('syris_calling_code', nextCalling)
          setTimezone(nextTimezone)
          setCookie('syris_timezone', nextTimezone)
          const nextCode = storedCurrency ? normalizeCurrency(storedCurrency) : nextCurrency
          setCurrencyCode(nextCode)
          localStorage.setItem('syris_currency', nextCode)
          setCookie('syris_currency', nextCode)
          setCurrencySymbol(storedCurrency ? symbolMap[nextCode] : nextSymbol || symbolMap[nextCode])
          localStorage.setItem(
            'syris_geo',
            JSON.stringify({
              country_code: code,
              country_calling_code: nextCalling,
              currency: nextCurrency,
              currency_symbol: nextSymbol,
              timezone: nextTimezone,
              languages: data?.languages || [],
            })
          )
          if (!storedLang) {
            const preferredLangs = Array.isArray(data?.languages) ? data.languages : []
            const prefersFrench = preferredLangs.some((lang: string) => String(lang).toLowerCase().startsWith('fr'))
            const inferred = prefersFrench || (code && francophoneCountries.has(code)) ? 'fr' : 'en'
            setLanguageState(inferred)
            localStorage.setItem('syris_lang', inferred)
            document.documentElement.lang = inferred
            setCookie('syris_lang', inferred)
          }
        })
        .catch(() => {})
    }
  }, [country])

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang)
    if (typeof window !== 'undefined') {
      localStorage.setItem('syris_lang', lang)
    }
    document.documentElement.lang = lang
    setCookie('syris_lang', lang)
  }, [])

  const setCurrency = useCallback((code: CurrencyCode) => {
    const nextCode = normalizeCurrency(code)
    setCurrencyCode(nextCode)
    if (typeof window !== 'undefined') {
      localStorage.setItem('syris_currency', nextCode)
    }
    setCookie('syris_currency', nextCode)
    setCurrencySymbol(symbolMap[nextCode])
  }, [])

  const value = useMemo(
    () => ({ language, country, currencyCode, currencySymbol, callingCode, timezone, setLanguage, setCurrency }),
    [language, country, currencyCode, currencySymbol, callingCode, timezone, setLanguage, setCurrency]
  )

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

export function useLanguage() {
  const ctx = useContext(LanguageContext)
  if (!ctx) {
    throw new Error('useLanguage must be used within LanguageProvider')
  }
  return ctx
}
  const symbolMap: Record<CurrencyCode, string | null> = { EUR: '€', USD: '$', TND: 'TND' }
